import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy } from "lucide-react";

interface Props {
  tournamentId: string;
  players: Array<{ user_id: string; profile: { full_name: string; level: number } }>;
}

export default function KnockoutBracket({ tournamentId, players }: Props) {
  const { data: brackets = [], isLoading } = useQuery({
    queryKey: ["tournament-brackets", tournamentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tournament_brackets" as any)
        .select("*")
        .eq("tournament_id", tournamentId)
        .order("round")
        .order("match_number");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const profileMap = new Map(players.map((p) => [p.user_id, p.profile]));
  const getName = (id: string | null) => {
    if (!id) return "TBD";
    return profileMap.get(id)?.full_name || id.slice(0, 8);
  };

  if (isLoading) return <Skeleton className="h-60" />;
  if (brackets.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-6">Inget bracket genererat ännu</p>;
  }

  const maxRound = Math.max(...brackets.map((b: any) => b.round));
  const rounds = Array.from({ length: maxRound }, (_, i) => i + 1);

  const getRoundLabel = (round: number) => {
    if (round === maxRound) return "Final";
    if (round === maxRound - 1) return "Semifinal";
    if (round === maxRound - 2 && maxRound >= 3) return "Kvartsfinal";
    return `Omgång ${round}`;
  };

  return (
    <div className="overflow-x-auto pb-4">
      <div className="flex gap-0 min-w-max py-4">
        {rounds.map((round, roundIdx) => {
          const matches = brackets.filter((b: any) => b.round === round);
          const roundLabel = getRoundLabel(round);
          // Gap between matches increases with each round for alignment
          const matchGap = Math.pow(2, roundIdx) * 16;

          return (
            <div key={round} className="flex flex-col" style={{ minWidth: 220 }}>
              <p className="text-xs font-semibold text-muted-foreground text-center mb-3 uppercase tracking-wider">
                {roundLabel}
              </p>
              <div
                className="flex flex-col justify-around flex-1"
                style={{ gap: `${matchGap}px` }}
              >
                {matches.map((m: any, matchIdx: number) => {
                  const hasWinner = !!m.winner_id;
                  return (
                    <div key={m.id} className="relative flex items-center">
                      {/* Connector line to the right */}
                      {roundIdx < rounds.length - 1 && (
                        <div className="absolute right-0 top-1/2 w-4 h-px bg-border translate-x-full" />
                      )}
                      {/* Connector line from the left */}
                      {roundIdx > 0 && (
                        <div className="absolute left-0 top-1/2 w-4 h-px bg-border -translate-x-full" />
                      )}

                      <Card className={`w-[200px] mx-4 overflow-hidden ${
                        hasWinner ? "border-primary/40 shadow-sm" : "border-border"
                      }`}>
                        <CardContent className="p-0">
                          {[m.team1_id, m.team2_id].map((tid: string | null, idx: number) => {
                            const isWinner = hasWinner && m.winner_id === tid;
                            const isLoser = hasWinner && m.winner_id !== tid && tid;
                            return (
                              <div
                                key={idx}
                                className={`flex items-center justify-between px-3 py-2.5 text-sm transition-colors ${
                                  idx === 0 ? "border-b border-border" : ""
                                } ${
                                  isWinner
                                    ? "bg-primary/10 font-semibold text-primary"
                                    : isLoser
                                    ? "text-muted-foreground bg-muted/30"
                                    : "text-foreground"
                                }`}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  {isWinner && (
                                    <div className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                                  )}
                                  <span className={`truncate ${!tid ? "italic text-muted-foreground" : ""}`}>
                                    {getName(tid)}
                                  </span>
                                </div>
                                {isWinner && <Trophy className="h-3.5 w-3.5 text-primary shrink-0" />}
                              </div>
                            );
                          })}
                          {m.score && (
                            <div className="text-center py-1 bg-muted/20 border-t border-border">
                              <span className="text-xs font-mono text-muted-foreground">{m.score}</span>
                            </div>
                          )}
                          {m.scheduled_time && !m.score && (
                            <div className="text-center py-1 bg-muted/20 border-t border-border">
                              <span className="text-[10px] text-muted-foreground">
                                {new Date(m.scheduled_time).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  );
                })}
              </div>
              {/* Vertical connector lines */}
              {roundIdx < rounds.length - 1 && (
                <div className="absolute" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
